import './StandardCalendar.scss';
import { useEffect, useState } from 'react';
import StandardCalendarDate from './StandardCalendarDate/StandardCalendarDate';
import StandardCalendarLegend from '../StandardCalendarLegend/StandardCalendarLegend';
import { mapPairingSegments, checkCarryOutOfWeekEvent, combinePairingSegments, addCarryInEvents, getUpdatedCarryOutOfWeekEvent} from './StandardCalendarHelper';
import { WEEKDAY_NAMES_SHORT } from '../../constants/dates';
import { RELEASE_TYPE, STD_CAL_CELL_HEIGHT } from '../../constants/myBlockMonth';
import { convertToDateObject, convertToDateTime, formatDate, getEasternTime, getNextDays, getNumberOfDays, getPreviousDays, getPreviousWeek } from '../../helpers/dateHelpers';
import { formatNumToTime } from '../../helpers/timeHelpers';
import { StandardCalendarDates } from '../../models/MyBlockMonthInterface';
import helper from '../../pages/MyBlockMonth/intersectingElementsHelper';
import { getEventCapsuleWidth, SPECIAL_EVENT_TYPES } from '../../pages/MyBlockMonth/myBlockMonthHelper';
import { useMyBlockMonthProvider } from '../../providers/MyBlockMonthProvider';

const StandardCalendar = ({
  actualView,
  checked,
  eventList,
  holidayEventList,
  reservePilot,
  noData,
  document,
  window
}) => {
  const myBlockMonthContext = useMyBlockMonthProvider();
  const [datesArray, setDatesArray] = useState<StandardCalendarDates[]>([]);
  const [updatedEventList , setUpdatedEventList] = useState<any>([]);
  const { checkIntersectingElements, checkElementsOverFlow, checkElementOverflowResize } = helper(document);

  useEffect(()=>{
    if(!eventList.length) return;
    const dates = getBlockMonthDates();
    let events = addPairingDetailsToEvent(eventList);
    events = handleCarryOutOfWeekEvents(events, dates);
    setUpdatedEventList(events);
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[eventList]);

  useEffect(() => {
    const dates = getBlockMonthDates();
    if(noData) {
      assignDateCellHeight(dates);
      setDatesArray(dates);
    } else {
      assignEventsWidth(updatedEventList);
      assignEventHeightLevel(updatedEventList);
      let datesArr = mapEventsToDates(dates, updatedEventList);
      assignDateCellHeight(datesArr);
      setDatesArray(datesArr);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedEventList])

  useEffect(() => {
    if(!datesArray.length || !eventList.length) return;
    setTimeout(() => {
      const calendarContainer = document.getElementsByClassName('c-dates-grid')[0];
      if(calendarContainer && updatedEventList.length > 0) {
        checkIntersectingElements(calendarContainer, updatedEventList);
        checkElementsOverFlow(calendarContainer);
      }
    }, 100)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datesArray])


  const addPairingDetailsToEvent = (events: any[]) => {
    const updatedEvents = events.map(event => {
      if(event.pairingDetails) {
        return ({
          ...event,
          pairingSegments: mapPairingSegments(event, combinePairingSegments(event))
        })
      } else return event; 
    })
    return updatedEvents; 
  }

  /**
   * Set the date cell height based on the max level of each of the events on that date
   * @param datesArr 
   */
    const assignDateCellHeight = (datesArr) => {
    datesArr.forEach(date => {
      if(!date?.events.length) date.height = STD_CAL_CELL_HEIGHT.ONE;
      else {
        const maxLevel = date.events.reduce((a, b) => a.level > b.level ? a : b).level;
        date.height = maxLevel > 1 ? maxLevel > 2 ? STD_CAL_CELL_HEIGHT.THREE : STD_CAL_CELL_HEIGHT.TWO : STD_CAL_CELL_HEIGHT.ONE;
      }
    })
  }

  const assignEventHeightLevel = (events) => {
    events.forEach(event => {
      if(SPECIAL_EVENT_TYPES.includes(event.activityType)) {
        const eventStartTime = convertToDateTime(event.start_date, formatNumToTime(event.checkIn.value));
        const eventEndTime = convertToDateTime(event.end_date, formatNumToTime(event.checkOut.value));
        const eventsToCompare = events.filter(compareEvent => compareEvent.unique_id !== event.unique_id);
        eventsToCompare.forEach(compareEvent => {
            const compareEventStartTime = convertToDateTime(compareEvent.start_date, formatNumToTime(compareEvent.checkIn.value));
            const compareEventEndTime = convertToDateTime(compareEvent.end_date, formatNumToTime(compareEvent.checkOut.value));
            if(compareEventStartTime === eventStartTime || 
              (compareEventStartTime > eventStartTime && compareEventStartTime < eventEndTime) || 
              (eventStartTime > compareEventStartTime && eventStartTime < compareEventEndTime)
              ) {
              if(compareEvent.level === 1) event.level = 2;
              if(compareEvent.level === 2) event.level = 3;
            } 
        })
      }
    })
  }

  const assignEventsWidth = (events: any[]) => {
    events.forEach(event => {
      const eventStartDateTime = event.hasStartRelease && event.release.findIndex(rel => rel.releaseType === RELEASE_TYPE.START) !== -1 ? convertToDateTime(event.release[event.release.findIndex(rel => rel.releaseType === RELEASE_TYPE.START)].start_date, formatNumToTime(event.release[event.release.findIndex(rel => rel.releaseType === RELEASE_TYPE.START)].checkIn.value)) : convertToDateTime(event.start_date, formatNumToTime(event.checkIn.value));
      const eventEndDateTime = event.hasEndRelease && event.release.findIndex(rel => rel.releaseType === RELEASE_TYPE.END) !== -1 ? convertToDateTime(event.release[event.release.findIndex(rel => rel.releaseType === RELEASE_TYPE.END)].end_date, formatNumToTime(event.release[event.release.findIndex(rel => rel.releaseType === RELEASE_TYPE.END)].checkOut.value)) : convertToDateTime(event.end_date, formatNumToTime(event.checkOut.value));
      event.stdCalWidthPct = getEventCapsuleWidth(100, eventStartDateTime, eventEndDateTime);
    })
  }

  /**
   * @returns an array of StandardCalendarDates[] objects
   */
  const getBlockMonthDates = () => {
    const startDay = convertToDateObject(myBlockMonthContext.myBlockMonthData.blockDates?.start, '00:00');
    const endDay = convertToDateObject(myBlockMonthContext.myBlockMonthData.blockDates?.end, '00:00');
    let currentDate = formatDate(getEasternTime());
    let blockDays: string[] = getNumberOfDays(startDay, endDay);
    blockDays = getPreviousDays(blockDays, blockDays[0]);
    blockDays = getNextDays(blockDays, blockDays[blockDays.length - 1]);

    if(eventList.length) {
      const firstEventStartDateTime = convertToDateTime(eventList[0].start_date, formatNumToTime(eventList[0].checkIn.value));
      const firstCalendarDateStartTime = convertToDateTime(blockDays[0], '00:00');
      if(firstEventStartDateTime < firstCalendarDateStartTime) getPreviousWeek(blockDays, blockDays[0]);
    }

    const datesArr = blockDays.map(day => {
      return {
        'day': day,
        'events': [],
        'isToday': currentDate === day
      }
    });
    
    return datesArr;
  }

  const handleCarryOutOfWeekEvents = (events: any[], blockMonthDates: any[]) => { 
    let updatedEvents = events.map(event => {
      return {
        ...event,
        carryOut: checkCarryOutOfWeekEvent(event),
        level: 1,
      }
    }) 

    const carryOutOfWeekEvents = updatedEvents.filter(event => event.carryOut);
    if(carryOutOfWeekEvents.length) {
      addCarryInEvents(events, carryOutOfWeekEvents, updatedEvents, blockMonthDates);
      updatedEvents.sort((x, y) => x.startDateDaysTime - y.startDateDaysTime);
      const updatedCarryOutEvents = updatedEvents.map(event => { return getUpdatedCarryOutOfWeekEvent(event, blockMonthDates) });
      return updatedCarryOutEvents; 
    }

    return updatedEvents; 
  } 

  /**
   * 
   * @param datesArr 
   * @returns this method prepares the daysArr which has events for each day and detailed info on event types which can be looped to populate events on * each day
   */
  const mapEventsToDates = (datesArr, activities) => {
    datesArr.forEach(date => {
      const dateTime = convertToDateTime(date.day, '00:00');
      activities.length && activities.forEach(activity => {
        const activityStartTime = activity.hasStartRelease && !activity.carryIn? convertToDateTime(activity.release[0].start_date, '00:00') : convertToDateTime(activity.start_date, '00:00');
        const activityEndTime = activity.hasEndRelease ? convertToDateTime(activity.release[activity.release.length-1].end_date, '23:59') : convertToDateTime(activity.end_date, '23:59');

        /* if the date falls between the event's start and end time, add the event to this date */
        if(dateTime >= activityStartTime && dateTime <= activityEndTime) {
          date.events.push({
            ...activity,   
            specialEvent: SPECIAL_EVENT_TYPES.includes(activity.activityType),
            originalStartDate: activity.hasStartRelease? activity.release[0].start_date : activity.start_date,
          });
        }
      })
    })
    return datesArr;
  }

  function windowResize() {
    if(!eventList.length) return;
    setTimeout(() => {
      const calendarContainer = document.getElementsByClassName('c-dates-grid')[0];
      if(calendarContainer && updatedEventList.length > 0) {
        checkIntersectingElements(calendarContainer, updatedEventList);
        checkElementOverflowResize('c-pairing-route-details', 'c-pairing-route-details--wrap', calendarContainer.getBoundingClientRect());
      }
    }, 100)
  }

  window.addEventListener('resize', windowResize);

  return (
    <div className="c-standard-calendar" id="std-calendar">
      <div className="c-header-week">
        {WEEKDAY_NAMES_SHORT.map((day, index) => <p className="header-week__day" key={index}>{day}</p>)}
      </div>
      <div className="c-dates-grid">
        {datesArray.map((day, index) => { 
          return (
            <StandardCalendarDate 
              actualView={actualView} 
              checked={checked} 
              day={day} 
              holidayEventList={holidayEventList} 
              noData={noData}
              document={document}
              key={index}
            />
          )
        })}
      </div>
      <StandardCalendarLegend eventList={eventList} reservePilot={reservePilot} noData={noData} />
    </div>
  );
}

export default StandardCalendar;